import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  FilmStrip,
  Camera,
  GridFour,
  Palette,
  ArrowsClockwise,
  Sun,
  Crosshair,
  Flask,
  Sparkle,
  Printer,
  CheckCircle,
  Target,
  Star,
  Gauge,
  Timer,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";
import GaleriaSocialCard from "@/components/GaleriaSocialCard";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const ICONS = {
  FilmStrip,
  Camera,
  GridFour,
  Palette,
  ArrowsClockwise,
  Sun,
  Crosshair,
  Flask,
  Sparkle,
  Target,
  Star,
  Timer,
};

const DIFF_BADGE = {
  beginner: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  intermediate: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  advanced: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const CONTENT = {
  en: {
    page_title: "Film Photography (35mm)",
    page_sub: "The analog revival is real — photographers everywhere are picking up 35mm SLRs again for their tactile process, timeless grain, and the discipline of making every one of 36 frames count. This guide takes you from loading your first roll to metering without a screen, picking the right film, and developing your shots — plus a hands-on look at the most beloved manual cameras, including the Canon AE-1 Program.",
    download: "Download as PDF",
    generating: "Generating...",
    pro_tip_label: "Pro tip",
    step_label: "Steps",

    sections: [
      {
        id: "why-film",
        icon: "FilmStrip",
        title: "1. Why Shoot Film Today?",
        intro: "In a world of infinite digital frames, film forces intention. With only 24 or 36 exposures, you slow down, compose deliberately, and learn exposure deeply because there's no instant screen to chimp. The look is real too — organic grain, gentle highlight roll-off, and color science (Portra, Gold, Ektar) that people still emulate with digital presets.",
        steps: [
          "Discipline: a limited roll trains you to nail exposure and composition BEFORE pressing the shutter.",
          "Aesthetic: true optical grain and highlight handling that's hard to fake digitally.",
          "Cost-effective entry: a used mechanical SLR + a 50mm lens often costs less than one modern lens.",
          "Tactile joy: manual focus, the wind-lever, the shutter clunk — a slower, more rewarding process.",
        ],
        tip: "Don't romanticize away the basics: film still obeys the exposure triangle. Everything you know about aperture, shutter, and ISO applies — you just commit to one ISO for the whole roll.",
      },
      {
        id: "formats",
        icon: "GridFour",
        title: "2. Film Formats & ASA/ISO",
        intro: "Film comes in physical sizes (formats) and speeds (ASA/ISO). 35mm is the gold standard for beginners: cheap, available everywhere, and used by every camera in this guide. ISO (called ASA on older film) sets the film's fixed sensitivity for the entire roll.",
        steps: [
          "35mm (135): 24 or 36 frames per roll. The most affordable and available — start here.",
          "120 (medium format): larger negatives, stunning detail, used by cameras like the Hasselblad and Mamiya. Pricier per shot.",
          "Large format (4x5, 8x10 sheet film): one giant negative per shot on a view camera — breathtaking resolution and tilt/shift movements, but slow, costly, and advanced. A bucket-list format, not a starting point.",
          "Instant (Polaroid / Instax): print on the spot, no developing — fun but low control.",
          "ASA = ISO. ISO 100–200 = fine grain for bright light; ISO 400 = the versatile all-rounder; ISO 800–3200 = low light, more visible grain.",
          "You pick ONE ISO per roll and meter for it the whole time — you cannot change it shot to shot like digital.",
        ],
        tip: "For your first rolls, buy ISO 400 color negative film (e.g. Kodak Gold 200, Kodak Ultramax 400, or Fujifilm). It's forgiving of exposure mistakes and works indoors and out.",
      },
      {
        id: "film-types",
        icon: "Palette",
        title: "3. Types of Film: Color, B&W & Slide",
        intro: "Three families of film each give a distinct look and require different handling at the lab. Knowing which to load shapes your whole result before you even shoot.",
        steps: [
          "Color Negative (C-41): the most common and forgiving. Wide exposure latitude — overexpose by a stop and it still looks great. Examples: Kodak Gold, Portra, Ultramax, Fuji C200.",
          "Black & White: timeless, high latitude, can be developed at home easily. Examples: Ilford HP5 400, Kodak Tri-X 400, Ilford FP4.",
          "Slide / Transparency (E-6): vivid, punchy color and fine grain, but VERY little latitude — you must nail exposure within 1/2 stop. Examples: Kodak Ektachrome, Fuji Velvia.",
          "Match the lab: C-41 (color neg) and E-6 (slide) need different chemistry — tell your lab which you shot.",
        ],
        tip: "Beginners: start with C-41 color negative or B&W. Save slide film (E-6) for when your metering is rock-solid — it's beautiful but unforgiving.",
      },
      {
        id: "loading",
        icon: "ArrowsClockwise",
        title: "4. Camera Anatomy + Loading & Rewinding Film",
        intro: "A manual SLR has a few key controls: the aperture ring (on the lens), the shutter-speed dial (top plate), the ASA/ISO dial, the film-advance lever, the rewind crank, and the shutter button. Loading film correctly is the #1 beginner anxiety — here's the foolproof sequence.",
        steps: [
          "Load in shade or shadow (not direct sun). Pull up the rewind knob to pop the back door open.",
          "Drop the film canister into the left chamber, then push the rewind knob back down to secure it.",
          "Pull the film leader across to the right and insert the tip into a slot on the take-up spool.",
          "Advance the lever once and fire the shutter; confirm the sprocket teeth engage the film's perforations and the film lies flat.",
          "Close the back. Gently turn the rewind crank to take up slack, then advance + fire until the frame counter reads '1'.",
          "Watch the rewind knob: it should ROTATE as you advance — that confirms film is actually moving.",
          "After the last frame, press the rewind-release button on the bottom, then turn the rewind crank until tension releases. Open the back and remove the canister.",
        ],
        tip: "If the rewind knob doesn't spin when you advance, the film never caught — open the back in shadow, re-seat the leader, and try again. Better to lose 2 frames than the whole roll.",
      },
      {
        id: "metering",
        icon: "Sun",
        title: "5. Exposure Without a Screen: Light Meter & Sunny 16",
        intro: "Film cameras either have a built-in light meter (a needle or LEDs in the viewfinder) or none at all. Either way, you must understand exposure before you shoot — there's no histogram to bail you out. The legendary 'Sunny 16' rule lets you expose correctly with no meter at all.",
        steps: [
          "Set the camera's ASA/ISO dial to match your loaded film (e.g. 400). This calibrates the built-in meter.",
          "With a meter: half-press or turn on the meter, then adjust aperture/shutter until the needle centers or the 'correct' LED lights.",
          "Sunny 16 rule: on a bright sunny day, set aperture to f/16 and shutter to 1/ISO (e.g. ISO 400 → 1/400 ≈ 1/500s).",
          "Then open up for less light: f/11 hazy sun, f/8 overcast, f/5.6 heavy overcast, f/4 open shade or sunset.",
          "Keep shutter at 1/ISO and only change the aperture as light changes — simple and reliable.",
        ],
        tip: "Old meters run on batteries that may be dead or discontinued (e.g. mercury PX625). If the needle doesn't move, learn Sunny 16 or use a free phone light-meter app — your camera will still shoot perfectly in full manual.",
      },
      {
        id: "focus",
        icon: "Crosshair",
        title: "6. Manual Focus & Depth of Field",
        intro: "Almost every classic SLR is manual focus. The viewfinder has focusing aids that make it fast and accurate once you learn them — and because there's no autofocus, you control exactly what's sharp.",
        steps: [
          "Split-prism: the center circle splits an image in two; rotate the focus ring until the two halves line up perfectly.",
          "Microprism collar: the ring around the center shimmers when out of focus and snaps clear when sharp.",
          "Depth of field: same rules as digital — f/2 isolates a subject with blur, f/8–f/16 keeps a scene sharp.",
          "Use the lens's DOF scale (the markings around the focus ring) to 'zone focus' for street shooting — set f/8 and a hyperfocal distance and everything from ~2m to infinity is sharp.",
        ],
        tip: "Diopter & glasses: if the split-prism never looks crisp, the viewfinder may need a diopter correction or you simply need to focus the focusing screen to your eye. Focus on a hard edge with strong contrast for the fastest lock.",
      },
      {
        id: "develop",
        icon: "Flask",
        title: "7. Developing & Digitizing Your Shots",
        intro: "A latent image lives on your film until it's developed. You have three paths — lab, home, or hybrid — and a final 'scan' step turns the negatives into digital files you can share (for example, on Galería Social).",
        steps: [
          "Lab develop + scan: the easiest route. Drop the roll at a lab or mail-in service; they develop and return digital scans + (optionally) prints.",
          "Develop at home: B&W is beginner-friendly with a daylight tank, chemicals, and a changing bag. Color (C-41) is doable but more temperature-sensitive.",
          "Scanning: use a dedicated film scanner, a flatbed with film holders, or 'DSLR scanning' (photograph the backlit negative with a macro lens and invert it).",
          "Always ask the lab for high-resolution scans if you plan to print large or edit heavily.",
        ],
        tip: "Label your rolls (camera, film, ISO, date) the moment you finish them. Exposed film looks identical to fresh film — a single mix-up can ruin a shoot.",
      },
      {
        id: "reciprocity",
        icon: "Timer",
        title: "8. Reciprocity Failure & Long Exposures",
        intro: "Film's sensitivity isn't constant at very long shutter speeds. Past roughly 1 second, the emulsion responds less and less efficiently to dim light — so the meter's 'correct' reading actually underexposes. This is called reciprocity failure, and you fix it by adding extra exposure time (or stops). Each film fails at a different rate, and color films can also shift color and need filtration.",
        steps: [
          "When it matters: only for metered times longer than ~1 second (night scenes, light trails, dim interiors, pinhole). Below 1s you can ignore it.",
          "What happens: the longer the metered time, the more EXTRA exposure you must add — the error grows non-linearly.",
          "Rule of thumb: a metered 1s might need ~2s; a metered 10s might need ~30s+; a metered 60s can balloon to several minutes, depending on the film.",
          "Use the datasheet: every film has its own reciprocity chart or formula (corrected = metered^p). Ilford films use p≈1.31; Fomapan fails heavily (p≈1.43); Kodak T-Max is famously reciprocity-resistant.",
          "Easier fix in stops: add roughly +1 stop at 1s, +2 stops around 10s, +3 stops near a minute — then open aperture or lengthen time to suit.",
          "Color shifts: long exposures on color film can drift green/blue and may need color-correction filters; shoot a test or bracket.",
        ],
        tip: "The simplest workflow: meter normally, and if the time is over 1 second, switch to a longer shutter (or Bulb with a cable release and watch) and apply the film's correction. When unsure, BRACKET — give one frame the metered time and another with the correction; film latitude often saves you. Try our interactive Reciprocity calculator in the Tools tab.",
      },
    ],

    filmstock_title: "Which Film to Buy",
    filmstock_sub: "A quick buying guide. Start with an affordable ISO 400 color negative, then branch out as your eye develops.",
    filmstock_h_name: "Film",
    filmstock_h_type: "Type",
    filmstock_h_use: "Best for",
    filmstock_h_grain: "Grain",
    filmstock_h_price: "Price",
    filmstock_rows: [
      { name: "Kodak Gold 200", type: "Color neg (C-41)", use: "Daylight, everyday, warm tones", grain: "Fine", price: "$" },
      { name: "Kodak Ultramax 400", type: "Color neg (C-41)", use: "All-round, indoor + outdoor", grain: "Medium", price: "$" },
      { name: "Fujicolor 200", type: "Color neg (C-41)", use: "Budget everyday, cooler tones", grain: "Medium", price: "$" },
      { name: "Kodak Portra 400", type: "Color neg (C-41)", use: "Portraits, natural skin tones", grain: "Fine", price: "$$$" },
      { name: "Kodak Ektar 100", type: "Color neg (C-41)", use: "Landscapes, vivid saturation", grain: "Very fine", price: "$$" },
      { name: "Ilford HP5 Plus 400", type: "B&W", use: "Versatile, push-friendly, street", grain: "Medium", price: "$" },
      { name: "Kodak Tri-X 400", type: "B&W", use: "Classic high-contrast look", grain: "Medium-high", price: "$$" },
      { name: "Kodak Ektachrome E100", type: "Slide (E-6)", use: "Vivid color, projection (advanced)", grain: "Very fine", price: "$$$" },
    ],

    develop_title: "Where to Develop Your Roll",
    develop_sub: "You don't need a darkroom. Pick the route that fits your budget and patience — always ask for high-resolution scans.",
    develop_h_route: "Option",
    develop_h_time: "Turnaround",
    develop_h_cost: "Cost",
    develop_rows: [
      { route: "Local 1-hour / pro lab", note: "Easiest; hand it over and pick up scans", time: "Same day – 2 days", cost: "$$" },
      { route: "Mail-in service", note: "Cheaper; great for B&W and color", time: "1 – 2 weeks", cost: "$ – $$" },
      { route: "Home develop — B&W", note: "Most control; needs a tank, chemicals, changing bag", time: "~1 hour", cost: "$ (after setup)" },
      { route: "Home develop — color C-41", note: "Doable but temperature-sensitive", time: "~1 hour", cost: "$ (after setup)" },
    ],

    cameras_title: "The Classic Manual Cameras",
    cameras_sub: "These 35mm SLRs defined the manual era and are still affordable, repairable, and a joy to shoot. Any of them is a fantastic first film camera — paired with a 50mm f/1.8 'nifty fifty', you have a complete kit.",

    featured: {
      label: "Featured Case Study",
      name: "Canon AE-1 Program",
      type: "1981 · Aperture/Shutter Priority + Program + Manual · FD mount",
      blurb: "The AE-1 Program is one of the best-selling SLRs of all time and a near-perfect learning camera: it offers a fully-automatic Program mode for beginners AND full Manual for when you want control. The bright viewfinder, reliable electronics, and huge selection of cheap FD lenses make it the camera many photographers (and your instructor) recommend first.",
      specs: [
        { k: "Mount", v: "Canon FD (huge cheap lens selection)" },
        { k: "Modes", v: "Program, Shutter Priority (Tv), Manual" },
        { k: "Shutter", v: "2s – 1/1000s + Bulb" },
        { k: "Meter", v: "Built-in TTL, needs a 4LR44 / A544 6V battery" },
        { k: "ISO range", v: "ASA 12 – 3200" },
      ],
      settings_label: "Recommended starting setup",
      settings: [
        { k: "Film", v: "Kodak Gold 200 or Ultramax 400 (color neg)" },
        { k: "Lens", v: "Canon FD 50mm f/1.8" },
        { k: "Mode", v: "'Program' (green 'P') while learning, then Manual" },
        { k: "Daylight", v: "f/8, 1/500s, ISO set to your film" },
      ],
      steps_label: "Quick start on the AE-1 Program",
      steps: [
        "Install a fresh 4LR44 (A544) 6V battery in the grip — the camera barely works without it.",
        "Set the ASA dial to your film speed (lift and turn the ring around the shutter dial).",
        "For Program mode: set the lens aperture ring to 'A' and the shutter dial to 'PROGRAM'. The camera picks everything — just focus and shoot.",
        "For Manual: pick a shutter speed on the dial and an aperture on the lens; match the viewfinder needle to the desired aperture.",
        "Focus with the split-prism in the center, advance the lever, and fire.",
      ],
    },

    cameras: [
      { name: "Canon AE-1 (original, 1976)", years: "Shutter Priority + Manual", type: "Canon FD", note: "The AE-1 Program's predecessor — same legendary reliability, Shutter Priority + Manual, no Program mode." },
      { name: "Minolta X-700 (1981)", years: "Program + Aperture Priority + Manual", type: "Minolta MD", note: "Minolta's flagship of the X-series; bright viewfinder, excellent Program mode, beloved by students. (Sibling models: X-300/X-370.)" },
      { name: "Minolta SRT 101 (1966)", years: "Fully mechanical + match-needle meter", type: "Minolta SR/MC", note: "A tank — all-mechanical, shoots without a battery (battery only powers the meter). Built to last forever." },
      { name: "Pentax Program A / Program Plus (1984)", years: "Program + Aperture Priority + Manual", type: "Pentax K", note: "Compact, easy Program automation and a clear LED viewfinder; takes the vast, affordable K-mount lens lineup." },
      { name: "Pentax K1000 (1976)", years: "Fully manual + match-needle meter", type: "Pentax K", note: "THE classic student camera — dead simple, indestructible, manual-only. Forces you to learn exposure." },
      { name: "Olympus OM-1 (1972)", years: "Fully mechanical + match-needle meter", type: "Olympus OM", note: "Famously tiny and light, gorgeous big viewfinder, all-mechanical shutter. A travel-photography legend." },
      { name: "Olympus OM-10 (1979)", years: "Aperture Priority (+ Manual adapter)", type: "Olympus OM", note: "Lighter, electronic, Aperture-Priority auto; great affordable entry to the OM system." },
      { name: "Nikon FM2 (1982)", years: "Fully mechanical + Manual", type: "Nikon F", note: "Pro-grade, all-mechanical, 1/4000s shutter; bulletproof and battery-independent (meter aside)." },
      { name: "Nikon FE (1978)", years: "Aperture Priority + Manual", type: "Nikon F", note: "Aperture-Priority auto with a beautiful needle display; a refined, reliable everyday shooter." },
      { name: "Yashica FX-3 / FX-3 Super 2000", years: "Fully mechanical + Manual", type: "Contax/Yashica", note: "Affordable, all-mechanical, and accepts superb Zeiss C/Y lenses — a budget gateway to legendary glass." },
      { name: "Minolta Maxxum 300si (1995)", years: "Autofocus · Program + Aperture/Shutter Priority + Manual", type: "Minolta A (AF) mount", note: "An easy entry-level AUTOFOCUS film SLR — great for students coming from digital. Built-in flash, simple Program mode plus full Manual. (Known as Dynax 300si in Europe.)" },
      { name: "Canon A-1 (1978)", years: "Program + Aperture/Shutter Priority + Manual", type: "Canon FD", note: "An advanced, feature-rich sibling of the AE-1 — full multi-mode automation with a slick digital readout. Uses the same affordable FD lenses." },
    ],

    sunny16_title: "The Sunny 16 Cheat Sheet",
    sunny16_sub: "No meter? No problem. Set shutter speed to 1/ISO (e.g. 1/500s for ISO 400), then pick the aperture for the light:",
    sunny16_cond_label: "Lighting condition",
    sunny16_aperture_label: "Aperture",
    sunny16_rows: [
      { cond: "Bright sun, hard shadows", aperture: "f/16", note: "Beach, snow, midday sun" },
      { cond: "Slight haze / soft shadows", aperture: "f/11", note: "Thin clouds, weak shadows" },
      { cond: "Overcast, no shadows", aperture: "f/8", note: "Cloudy bright day" },
      { cond: "Heavy overcast", aperture: "f/5.6", note: "Dark grey sky" },
      { cond: "Open shade / sunset", aperture: "f/4", note: "Subject in shadow, golden hour" },
    ],
    sunny16_note: "Shutter rule: set shutter ≈ 1/ISO. ISO 100 → 1/125s · ISO 200 → 1/250s · ISO 400 → 1/500s. Then only the aperture changes with the light.",

    projects_title: "Practice Projects — Shoot, Self-Critique, Share",
    projects_sub: "Apply what you learned with these film-specific assignments. Each includes the objective, starting settings, tips, deliverables, and a self-critique checklist. Share your best frames on Galería Social.",
    difficulty_label: "Difficulty",
    objective_label: "Objective",
    settings_label: "Starting Settings",
    tips_label: "Tips",
    deliverables_label: "Deliverables",
    critique_label: "Self-Critique Checklist",
    diff_beginner: "Beginner",
    diff_intermediate: "Intermediate",
    diff_advanced: "Advanced",

    projects: [
      {
        id: "film_one_roll",
        icon: "FilmStrip",
        title: "1. One Roll, One Day",
        desc: "Shoot a single 36-exposure roll over one day. The constraint is the lesson — make every frame count.",
        difficulty: "beginner",
        relates: "Sections 4 (Loading), 5 (Metering)",
        objective: "One fully-exposed 36-frame roll documenting a single day, start to finish.",
        settings: "ISO 400 color neg | 50mm lens | Program or Manual | Meter or Sunny 16",
        tips: [
          "Slow down — think before each frame. You have 36, not 3,600.",
          "Vary your distance: wide context shots, medium, and close details.",
          "Note your settings for a few tricky frames so you can learn from the scans.",
          "Keep the camera with you all day; shoot the ordinary, not just the 'special'.",
        ],
        deliverables: "36 frames developed + scanned, presented as a small edit of your 8 best.",
        critique: [
          "Are most frames correctly exposed (not too dark/thin)?",
          "Is focus sharp where you intended?",
          "Does the set tell a story across the day?",
          "Did you vary composition and distance?",
        ],
      },
      {
        id: "film_sunny16",
        icon: "Sun",
        title: "2. Sunny 16 — No Meter Challenge",
        desc: "Shoot a half-roll using ONLY the Sunny 16 rule. Turn the meter off (or cover it) and trust the system.",
        difficulty: "intermediate",
        relates: "Section 5 (Metering & Sunny 16)",
        objective: "12 frames exposed entirely by Sunny 16 estimation, across different lighting conditions.",
        settings: "ISO 400 | Shutter 1/500s fixed | Aperture by condition (f/16 → f/4)",
        tips: [
          "Set shutter to 1/500s and leave it; change only the aperture.",
          "Bright sun = f/16, overcast = f/8, open shade = f/4.",
          "Shoot the same subject in sun and shade to see the aperture difference.",
          "Write down your guess for each frame, then compare to the scans.",
        ],
        deliverables: "12 frames + a short note comparing your guessed exposure to the actual result.",
        critique: [
          "Are the sunny frames well-exposed at f/16?",
          "Did you correctly open up in shade/overcast?",
          "Any frames too dark (under) or washed out (over)?",
          "Do you now trust Sunny 16 without a meter?",
        ],
      },
      {
        id: "film_bw_portrait",
        icon: "Palette",
        title: "3. Black & White Portrait",
        desc: "A roll of B&W portraits exploring light, tone, and shallow depth of field on film.",
        difficulty: "intermediate",
        relates: "Sections 3 (Film types), 6 (Focus & DOF)",
        objective: "5 strong B&W portraits of one or more subjects using window or open-shade light.",
        settings: "Ilford HP5 400 or Kodak Tri-X 400 | 50mm at f/2–f/2.8 | Meter for the face",
        tips: [
          "Meter off the subject's face, not the bright background.",
          "Open shade or soft window light flatters skin and avoids squinting.",
          "Focus on the near eye using the split-prism.",
          "Watch your shutter: keep it at 1/125s+ handheld to avoid blur.",
        ],
        deliverables: "5 edited B&W portrait scans showing intentional light and sharp eyes.",
        critique: [
          "Are the eyes razor sharp?",
          "Is the face correctly exposed (good midtones, detail in skin)?",
          "Does the shallow DOF separate subject from background?",
          "Is the tonal range pleasing (true blacks and clean whites)?",
        ],
      },
      {
        id: "film_double_exposure",
        icon: "Sparkle",
        title: "4. Creative — Double Exposure",
        desc: "Use your camera's multiple-exposure trick (or rewind-and-reshoot) to layer two images on one frame.",
        difficulty: "advanced",
        relates: "Sections 2 (Exposure), 5 (Metering)",
        objective: "3 successful double exposures combining two complementary scenes (e.g. a portrait + foliage).",
        settings: "ISO 100–200 | Underexpose EACH layer by ~1 stop so the combined frame isn't too bright",
        tips: [
          "If your camera lacks a multi-exposure lever, hold the rewind button while cocking the advance lever to NOT advance the film.",
          "Underexpose each exposure by ~1 stop so the stacked result balances out.",
          "Pair a dark subject area with a bright texture so both read clearly.",
          "Plan the composition: shoot the subject against dark, then the texture against light.",
        ],
        deliverables: "3 double-exposure frames + a note on what you layered and the exposure you used.",
        critique: [
          "Are both layers visible and intentional (not muddy)?",
          "Is the combined exposure balanced (not blown out)?",
          "Does the pairing feel creative, not random?",
          "Could you reproduce the effect on purpose now?",
        ],
      },
    ],

    closing: "Film won't replace your digital workflow — it complements it. The discipline of metering by hand, composing for one of 36 frames, and waiting for your scans makes you a sharper, more intentional photographer in EVERY medium. Load a roll, slow down, and enjoy the craft.",
  },

  es: {
    page_title: "Fotografía Análoga (35mm)",
    page_sub: "El renacer analogico es real — fotografos de todo el mundo vuelven a las SLR de 35mm por su proceso tactil, el grano atemporal y la disciplina de hacer que cada uno de los 36 cuadros cuente. Esta guia te lleva desde cargar tu primer rollo hasta medir la luz sin pantalla, elegir la pelicula correcta y revelar tus tomas — ademas de un repaso practico de las camaras manuales mas queridas, incluyendo la Canon AE-1 Program.",
    download: "Descargar como PDF",
    generating: "Generando...",
    pro_tip_label: "Tip pro",
    step_label: "Pasos",

    sections: [
      {
        id: "why-film",
        icon: "FilmStrip",
        title: "1. Por Que Disparar Film Hoy?",
        intro: "En un mundo de cuadros digitales infinitos, el film obliga a la intencion. Con solo 24 o 36 exposiciones, bajas el ritmo, compones deliberadamente y aprendes exposicion a fondo porque no hay pantalla instantanea para revisar. El look tambien es real — grano organico, suave transicion en altas luces y una ciencia de color (Portra, Gold, Ektar) que la gente aun imita con presets digitales.",
        steps: [
          "Disciplina: un rollo limitado te entrena a clavar exposicion y composicion ANTES de presionar el disparador.",
          "Estetica: grano optico real y manejo de altas luces dificil de falsificar en digital.",
          "Entrada economica: una SLR mecanica usada + un lente 50mm a menudo cuesta menos que un solo lente moderno.",
          "Placer tactil: enfoque manual, la palanca de avance, el 'clunk' del obturador — un proceso mas lento y gratificante.",
        ],
        tip: "No romantices los fundamentos: el film aun obedece el triangulo de exposicion. Todo lo que sabes de apertura, velocidad e ISO aplica — solo que te comprometes a un solo ISO para todo el rollo.",
      },
      {
        id: "formats",
        icon: "GridFour",
        title: "2. Formatos de Pelicula y ASA/ISO",
        intro: "La pelicula viene en tamaños fisicos (formatos) y velocidades (ASA/ISO). El 35mm es el estandar para principiantes: barato, disponible en todas partes y usado por cada camara de esta guia. El ISO (llamado ASA en peliculas antiguas) fija la sensibilidad de la pelicula para todo el rollo.",
        steps: [
          "35mm (135): 24 o 36 cuadros por rollo. El mas economico y disponible — empieza aqui.",
          "120 (medio formato): negativos mas grandes, detalle impresionante, usado por camaras como Hasselblad y Mamiya. Mas caro por toma.",
          "Gran formato (pelicula en hojas 4x5, 8x10): un solo negativo gigante por toma en una camara de banco — resolucion impresionante y movimientos tilt/shift, pero lento, costoso y avanzado. Un formato de lista de deseos, no para empezar.",
          "Instantanea (Polaroid / Instax): imprime al momento, sin revelado — divertido pero poco control.",
          "ASA = ISO. ISO 100–200 = grano fino para luz brillante; ISO 400 = el versatil todoterreno; ISO 800–3200 = poca luz, grano mas visible.",
          "Eliges UN ISO por rollo y mides para el todo el tiempo — no puedes cambiarlo toma a toma como en digital.",
        ],
        tip: "Para tus primeros rollos, compra pelicula negativo color ISO 400 (ej. Kodak Gold 200, Kodak Ultramax 400 o Fujifilm). Perdona errores de exposicion y funciona dentro y fuera.",
      },
      {
        id: "film-types",
        icon: "Palette",
        title: "3. Tipos de Pelicula: Color, B/N y Diapositiva",
        intro: "Tres familias de pelicula, cada una con un look distinto y manejo diferente en el laboratorio. Saber cual cargar define todo tu resultado antes incluso de disparar.",
        steps: [
          "Negativo Color (C-41): la mas comun y permisiva. Amplia latitud de exposicion — sobreexpon un paso y aun se ve genial. Ejemplos: Kodak Gold, Portra, Ultramax, Fuji C200.",
          "Blanco y Negro: atemporal, alta latitud, facil de revelar en casa. Ejemplos: Ilford HP5 400, Kodak Tri-X 400, Ilford FP4.",
          "Diapositiva / Transparencia (E-6): color vivido y punchy con grano fino, pero MUY poca latitud — debes clavar la exposicion dentro de 1/2 paso. Ejemplos: Kodak Ektachrome, Fuji Velvia.",
          "Avisa al laboratorio: C-41 (negativo color) y E-6 (diapositiva) usan quimica distinta — di cual disparaste.",
        ],
        tip: "Principiantes: empieza con negativo color C-41 o B/N. Deja la diapositiva (E-6) para cuando tu medicion sea solida — es preciosa pero implacable.",
      },
      {
        id: "loading",
        icon: "ArrowsClockwise",
        title: "4. Anatomia de la Camara + Cargar y Rebobinar el Rollo",
        intro: "Una SLR manual tiene pocos controles clave: el anillo de apertura (en el lente), el dial de velocidad (placa superior), el dial ASA/ISO, la palanca de avance, la manivela de rebobinado y el disparador. Cargar la pelicula correctamente es la ansiedad #1 del principiante — aqui la secuencia infalible.",
        steps: [
          "Carga en sombra (no al sol directo). Levanta la perilla de rebobinado para abrir la tapa trasera.",
          "Coloca el carrete de pelicula en la camara izquierda y empuja la perilla de rebobinado hacia abajo para asegurarlo.",
          "Jala la lengueta de pelicula hacia la derecha e inserta la punta en una ranura del carrete receptor.",
          "Avanza la palanca una vez y dispara; confirma que los dientes engranan las perforaciones y la pelicula queda plana.",
          "Cierra la tapa. Gira suave la manivela de rebobinado para tensar, luego avanza + dispara hasta que el contador marque '1'.",
          "Observa la perilla de rebobinado: debe GIRAR mientras avanzas — eso confirma que la pelicula se mueve.",
          "Tras el ultimo cuadro, presiona el boton de liberacion de rebobinado en la base y gira la manivela hasta que ceda la tension. Abre la tapa y saca el carrete.",
        ],
        tip: "Si la perilla de rebobinado no gira al avanzar, la pelicula nunca engancho — abre la tapa en sombra, recoloca la lengueta e intenta de nuevo. Mejor perder 2 cuadros que todo el rollo.",
      },
      {
        id: "metering",
        icon: "Sun",
        title: "5. Exposicion Sin Pantalla: Fotometro y Sunny 16",
        intro: "Las camaras de film tienen un fotometro integrado (una aguja o LEDs en el visor) o ninguno. En cualquier caso, debes entender la exposicion antes de disparar — no hay histograma que te salve. La legendaria regla 'Sunny 16' te deja exponer correctamente sin fotometro alguno.",
        steps: [
          "Ajusta el dial ASA/ISO de la camara a tu pelicula (ej. 400). Esto calibra el fotometro interno.",
          "Con fotometro: medio-presiona o enciende el medidor, luego ajusta apertura/velocidad hasta centrar la aguja o encender el LED 'correcto'.",
          "Regla Sunny 16: en un dia soleado brillante, pon apertura f/16 y velocidad 1/ISO (ej. ISO 400 → 1/400 ≈ 1/500s).",
          "Luego abre para menos luz: f/11 sol con bruma, f/8 nublado, f/5.6 muy nublado, f/4 sombra abierta o atardecer.",
          "Manten la velocidad en 1/ISO y solo cambia la apertura segun cambia la luz — simple y confiable.",
        ],
        tip: "Los fotometros viejos usan baterias que pueden estar muertas o descontinuadas (ej. mercurio PX625). Si la aguja no se mueve, aprende Sunny 16 o usa una app gratis de fotometro en el telefono — tu camara seguira disparando perfecto en manual.",
      },
      {
        id: "focus",
        icon: "Crosshair",
        title: "6. Enfoque Manual y Profundidad de Campo",
        intro: "Casi toda SLR clasica es de enfoque manual. El visor tiene ayudas de enfoque que lo hacen rapido y preciso una vez que las aprendes — y como no hay autofoco, controlas exactamente que sale nitido.",
        steps: [
          "Prisma partido: el circulo central divide la imagen en dos; gira el anillo de enfoque hasta que las mitades se alineen perfectamente.",
          "Collar de microprisma: el aro alrededor del centro titila cuando esta desenfocado y se aclara cuando enfoca.",
          "Profundidad de campo: mismas reglas que digital — f/2 aisla un sujeto con desenfoque, f/8–f/16 mantiene la escena nitida.",
          "Usa la escala de PDC del lente (marcas alrededor del anillo de enfoque) para 'zone focus' en calle — pon f/8 y una distancia hiperfocal y todo de ~2m a infinito queda nitido.",
        ],
        tip: "Dioptria y lentes: si el prisma partido nunca se ve nitido, el visor puede necesitar correccion de dioptria o ajustar la pantalla de enfoque a tu ojo. Enfoca en un borde duro con contraste fuerte para el bloqueo mas rapido.",
      },
      {
        id: "develop",
        icon: "Flask",
        title: "7. Revelado y Digitalizacion de Tus Tomas",
        intro: "Una imagen latente vive en tu pelicula hasta que se revela. Tienes tres caminos — laboratorio, casa o hibrido — y un paso final de 'escaneo' convierte los negativos en archivos digitales que puedes compartir (por ejemplo, en Galeria Social).",
        steps: [
          "Revelado + escaneo en laboratorio: la ruta mas facil. Deja el rollo en un lab o servicio por correo; revelan y devuelven escaneos digitales + (opcional) impresiones.",
          "Revelar en casa: el B/N es amigable para principiantes con un tanque de luz dia, quimicos y una bolsa de cambio. El color (C-41) es posible pero mas sensible a la temperatura.",
          "Escaneo: usa un escaner de pelicula dedicado, una cama plana con porta-negativos, o 'escaneo con DSLR' (fotografia el negativo retroiluminado con un lente macro e inviertelo).",
          "Pide siempre escaneos en alta resolucion si planeas imprimir grande o editar mucho.",
        ],
        tip: "Etiqueta tus rollos (camara, pelicula, ISO, fecha) en cuanto los termines. La pelicula expuesta se ve identica a la nueva — una sola confusion puede arruinar una sesion.",
      },
      {
        id: "reciprocity",
        icon: "Timer",
        title: "8. Fallo de Reciprocidad y Largas Exposiciones",
        intro: "La sensibilidad de la pelicula no es constante en velocidades de obturador muy lentas. Pasado aproximadamente 1 segundo, la emulsion responde cada vez menos a la luz tenue — asi que la lectura 'correcta' del fotometro en realidad subexpone. Esto se llama fallo de reciprocidad y se corrige anadiendo tiempo de exposicion extra (o pasos). Cada pelicula falla a un ritmo distinto, y las peliculas de color ademas cambian de tono y pueden necesitar filtros.",
        steps: [
          "Cuando importa: solo para tiempos medidos mayores a ~1 segundo (escenas nocturnas, estelas de luz, interiores oscuros, pinhole). Por debajo de 1s puedes ignorarlo.",
          "Que ocurre: cuanto mas largo el tiempo medido, mas exposicion EXTRA debes anadir — el error crece de forma no lineal.",
          "Regla general: 1s medido puede necesitar ~2s; 10s medidos pueden necesitar ~30s+; 60s medidos pueden dispararse a varios minutos, segun la pelicula.",
          "Usa la hoja de datos: cada pelicula tiene su grafico o formula (corregido = medido^p). Las peliculas Ilford usan p≈1.31; Fomapan falla mucho (p≈1.43); la Kodak T-Max resiste muy bien la reciprocidad.",
          "Correccion mas facil en pasos: anade ~+1 paso a 1s, +2 pasos cerca de 10s, +3 pasos cerca de un minuto — luego abre la apertura o alarga el tiempo segun convenga.",
          "Cambios de color: las largas exposiciones en pelicula de color pueden virar a verde/azul y necesitar filtros de correccion; haz una prueba o bracketing.",
        ],
        tip: "El flujo mas simple: mide normal, y si el tiempo supera 1 segundo, cambia a un obturador mas largo (o Bulb con cable disparador y cronometra) y aplica la correccion de la pelicula. Si dudas, haz BRACKETING — un cuadro con el tiempo medido y otro con la correccion; la latitud del film suele salvarte. Prueba nuestra calculadora interactiva de Reciprocidad en la pestaña Herramientas.",
      },
    ],

    filmstock_title: "Que Pelicula Comprar",
    filmstock_sub: "Una guia rapida de compra. Empieza con un negativo color ISO 400 economico y luego diversifica conforme desarrollas tu ojo.",
    filmstock_h_name: "Pelicula",
    filmstock_h_type: "Tipo",
    filmstock_h_use: "Ideal para",
    filmstock_h_grain: "Grano",
    filmstock_h_price: "Precio",
    filmstock_rows: [
      { name: "Kodak Gold 200", type: "Negativo color (C-41)", use: "Luz dia, diario, tonos calidos", grain: "Fino", price: "$" },
      { name: "Kodak Ultramax 400", type: "Negativo color (C-41)", use: "Todoterreno, interior + exterior", grain: "Medio", price: "$" },
      { name: "Fujicolor 200", type: "Negativo color (C-41)", use: "Economica diaria, tonos frios", grain: "Medio", price: "$" },
      { name: "Kodak Portra 400", type: "Negativo color (C-41)", use: "Retratos, tonos de piel naturales", grain: "Fino", price: "$$$" },
      { name: "Kodak Ektar 100", type: "Negativo color (C-41)", use: "Paisajes, saturacion vivida", grain: "Muy fino", price: "$$" },
      { name: "Ilford HP5 Plus 400", type: "B/N", use: "Versatil, admite push, calle", grain: "Medio", price: "$" },
      { name: "Kodak Tri-X 400", type: "B/N", use: "Look clasico de alto contraste", grain: "Medio-alto", price: "$$" },
      { name: "Kodak Ektachrome E100", type: "Diapositiva (E-6)", use: "Color vivido, proyeccion (avanzado)", grain: "Muy fino", price: "$$$" },
    ],

    develop_title: "Donde Revelar Tu Rollo",
    develop_sub: "No necesitas un cuarto oscuro. Elige la ruta que se ajuste a tu presupuesto y paciencia — pide siempre escaneos en alta resolucion.",
    develop_h_route: "Opcion",
    develop_h_time: "Tiempo",
    develop_h_cost: "Costo",
    develop_rows: [
      { route: "Lab local 1 hora / pro", note: "Lo mas facil; entregas y recoges escaneos", time: "Mismo dia – 2 dias", cost: "$$" },
      { route: "Servicio por correo", note: "Mas barato; ideal para B/N y color", time: "1 – 2 semanas", cost: "$ – $$" },
      { route: "Revelado en casa — B/N", note: "Mayor control; requiere tanque, quimicos, bolsa de cambio", time: "~1 hora", cost: "$ (tras equiparte)" },
      { route: "Revelado en casa — color C-41", note: "Posible pero sensible a la temperatura", time: "~1 hora", cost: "$ (tras equiparte)" },
    ],

    cameras_title: "Las Camaras Manuales Clasicas",
    cameras_sub: "Estas SLR de 35mm definieron la era manual y siguen siendo economicas, reparables y un placer de disparar. Cualquiera es una primera camara de film fantastica — junto a un 50mm f/1.8 'nifty fifty', tienes un kit completo.",

    featured: {
      label: "Caso Practico Destacado",
      name: "Canon AE-1 Program",
      type: "1981 · Prioridad de Apertura/Velocidad + Programa + Manual · Montura FD",
      blurb: "La AE-1 Program es una de las SLR mas vendidas de la historia y una camara de aprendizaje casi perfecta: ofrece un modo Programa totalmente automatico para principiantes Y Manual completo para cuando quieres control. El visor brillante, la electronica confiable y la enorme seleccion de lentes FD baratos la hacen la camara que muchos fotografos (y tu instructor) recomiendan primero.",
      specs: [
        { k: "Montura", v: "Canon FD (gran seleccion de lentes baratos)" },
        { k: "Modos", v: "Programa, Prioridad de Velocidad (Tv), Manual" },
        { k: "Obturador", v: "2s – 1/1000s + Bulb" },
        { k: "Fotometro", v: "TTL integrado, requiere bateria 4LR44 / A544 6V" },
        { k: "Rango ISO", v: "ASA 12 – 3200" },
      ],
      settings_label: "Configuracion inicial recomendada",
      settings: [
        { k: "Pelicula", v: "Kodak Gold 200 o Ultramax 400 (negativo color)" },
        { k: "Lente", v: "Canon FD 50mm f/1.8" },
        { k: "Modo", v: "'Programa' (la 'P' verde) al aprender, luego Manual" },
        { k: "Luz dia", v: "f/8, 1/500s, ISO ajustado a tu pelicula" },
      ],
      steps_label: "Inicio rapido en la AE-1 Program",
      steps: [
        "Instala una bateria fresca 4LR44 (A544) 6V en la empuñadura — la camara apenas funciona sin ella.",
        "Ajusta el dial ASA a la velocidad de tu pelicula (levanta y gira el anillo alrededor del dial de velocidad).",
        "Para modo Programa: pon el anillo de apertura del lente en 'A' y el dial de velocidad en 'PROGRAM'. La camara elige todo — solo enfoca y dispara.",
        "Para Manual: elige una velocidad en el dial y una apertura en el lente; iguala la aguja del visor a la apertura deseada.",
        "Enfoca con el prisma partido del centro, avanza la palanca y dispara.",
      ],
    },

    cameras: [
      { name: "Canon AE-1 (original, 1976)", years: "Prioridad de Velocidad + Manual", type: "Canon FD", note: "La predecesora de la AE-1 Program — misma fiabilidad legendaria, Prioridad de Velocidad + Manual, sin modo Programa." },
      { name: "Minolta X-700 (1981)", years: "Programa + Prioridad de Apertura + Manual", type: "Minolta MD", note: "El buque insignia de la serie X de Minolta; visor brillante, excelente modo Programa, querida por estudiantes. (Hermanas: X-300/X-370.)" },
      { name: "Minolta SRT 101 (1966)", years: "Totalmente mecanica + fotometro de aguja", type: "Minolta SR/MC", note: "Un tanque — totalmente mecanica, dispara sin bateria (la bateria solo alimenta el fotometro). Construida para durar para siempre." },
      { name: "Pentax Program A / Program Plus (1984)", years: "Programa + Prioridad de Apertura + Manual", type: "Pentax K", note: "Compacta, automatizacion Programa facil y visor LED claro; usa la enorme y economica linea de lentes montura K." },
      { name: "Pentax K1000 (1976)", years: "Totalmente manual + fotometro de aguja", type: "Pentax K", note: "LA camara clasica de estudiante — simplisima, indestructible, solo manual. Te obliga a aprender exposicion." },
      { name: "Olympus OM-1 (1972)", years: "Totalmente mecanica + fotometro de aguja", type: "Olympus OM", note: "Famosamente pequeña y ligera, visor grande precioso, obturador mecanico. Una leyenda de la fotografia de viaje." },
      { name: "Olympus OM-10 (1979)", years: "Prioridad de Apertura (+ adaptador Manual)", type: "Olympus OM", note: "Mas ligera, electronica, automatico de Prioridad de Apertura; gran entrada economica al sistema OM." },
      { name: "Nikon FM2 (1982)", years: "Totalmente mecanica + Manual", type: "Nikon F", note: "Grado profesional, totalmente mecanica, obturador 1/4000s; a prueba de balas e independiente de bateria (salvo el fotometro)." },
      { name: "Nikon FE (1978)", years: "Prioridad de Apertura + Manual", type: "Nikon F", note: "Automatico de Prioridad de Apertura con una bella aguja indicadora; una camara diaria refinada y confiable." },
      { name: "Yashica FX-3 / FX-3 Super 2000", years: "Totalmente mecanica + Manual", type: "Contax/Yashica", note: "Economica, totalmente mecanica y acepta excelentes lentes Zeiss C/Y — una puerta de entrada barata a vidrio legendario." },
      { name: "Minolta Maxxum 300si (1995)", years: "Autofoco · Programa + Prioridad de Apertura/Velocidad + Manual", type: "Montura Minolta A (AF)", note: "Una SLR de film con AUTOFOCO de entrada, ideal para estudiantes que vienen del digital. Flash integrado, modo Programa simple y Manual completo. (Conocida como Dynax 300si en Europa.)" },
      { name: "Canon A-1 (1978)", years: "Programa + Prioridad de Apertura/Velocidad + Manual", type: "Canon FD", note: "Una hermana avanzada y completa de la AE-1 — automatización multimodo con lectura digital. Usa los mismos lentes FD económicos." },
    ],

    sunny16_title: "La Hoja de Tips de Sunny 16",
    sunny16_sub: "Sin fotometro? Sin problema. Pon la velocidad en 1/ISO (ej. 1/500s para ISO 400), luego elige la apertura segun la luz:",
    sunny16_cond_label: "Condicion de luz",
    sunny16_aperture_label: "Apertura",
    sunny16_rows: [
      { cond: "Sol brillante, sombras duras", aperture: "f/16", note: "Playa, nieve, sol de mediodia" },
      { cond: "Bruma ligera / sombras suaves", aperture: "f/11", note: "Nubes delgadas, sombras debiles" },
      { cond: "Nublado, sin sombras", aperture: "f/8", note: "Dia nublado luminoso" },
      { cond: "Muy nublado", aperture: "f/5.6", note: "Cielo gris oscuro" },
      { cond: "Sombra abierta / atardecer", aperture: "f/4", note: "Sujeto en sombra, golden hour" },
    ],
    sunny16_note: "Regla de velocidad: pon velocidad ≈ 1/ISO. ISO 100 → 1/125s · ISO 200 → 1/250s · ISO 400 → 1/500s. Luego solo cambia la apertura con la luz.",

    projects_title: "Proyectos de Practica — Dispara, Auto-Critica, Comparte",
    projects_sub: "Aplica lo aprendido con estas tareas especificas de film. Cada una incluye objetivo, ajustes iniciales, tips, entregables y una lista de auto-critica. Comparte tus mejores cuadros en Galeria Social.",
    difficulty_label: "Dificultad",
    objective_label: "Objetivo",
    settings_label: "Ajustes Iniciales",
    tips_label: "Tips",
    deliverables_label: "Entregables",
    critique_label: "Lista de Auto-Critica",
    diff_beginner: "Principiante",
    diff_intermediate: "Intermedio",
    diff_advanced: "Avanzado",

    projects: [
      {
        id: "film_one_roll",
        icon: "FilmStrip",
        title: "1. Un Rollo, Un Dia",
        desc: "Dispara un solo rollo de 36 exposiciones durante un dia. La restriccion es la leccion — haz que cada cuadro cuente.",
        difficulty: "beginner",
        relates: "Secciones 4 (Cargar), 5 (Medicion)",
        objective: "Un rollo completo de 36 cuadros documentando un solo dia, de principio a fin.",
        settings: "ISO 400 negativo color | Lente 50mm | Programa o Manual | Fotometro o Sunny 16",
        tips: [
          "Baja el ritmo — piensa antes de cada cuadro. Tienes 36, no 3,600.",
          "Varia tu distancia: tomas amplias de contexto, medias y detalles cercanos.",
          "Anota tus ajustes en algunos cuadros dificiles para aprender de los escaneos.",
          "Lleva la camara contigo todo el dia; dispara lo ordinario, no solo lo 'especial'.",
        ],
        deliverables: "36 cuadros revelados + escaneados, presentados como una pequeña edicion de tus 8 mejores.",
        critique: [
          "La mayoria de cuadros estan bien expuestos (no muy oscuros/delgados)?",
          "El enfoque esta nitido donde lo pretendiste?",
          "El set cuenta una historia a lo largo del dia?",
          "Variaste composicion y distancia?",
        ],
      },
      {
        id: "film_sunny16",
        icon: "Sun",
        title: "2. Sunny 16 — Reto Sin Fotometro",
        desc: "Dispara medio rollo usando SOLO la regla Sunny 16. Apaga el fotometro (o cubrelo) y confia en el sistema.",
        difficulty: "intermediate",
        relates: "Seccion 5 (Medicion y Sunny 16)",
        objective: "12 cuadros expuestos enteramente por estimacion Sunny 16, en distintas condiciones de luz.",
        settings: "ISO 400 | Velocidad 1/500s fija | Apertura por condicion (f/16 → f/4)",
        tips: [
          "Pon la velocidad en 1/500s y dejala; cambia solo la apertura.",
          "Sol brillante = f/16, nublado = f/8, sombra abierta = f/4.",
          "Dispara el mismo sujeto en sol y en sombra para ver la diferencia de apertura.",
          "Anota tu estimacion para cada cuadro y luego comparala con los escaneos.",
        ],
        deliverables: "12 cuadros + una nota corta comparando tu exposicion estimada con el resultado real.",
        critique: [
          "Los cuadros soleados estan bien expuestos a f/16?",
          "Abriste correctamente en sombra/nublado?",
          "Algun cuadro muy oscuro (sub) o lavado (sobre)?",
          "Ya confias en Sunny 16 sin fotometro?",
        ],
      },
      {
        id: "film_bw_portrait",
        icon: "Palette",
        title: "3. Retrato en Blanco y Negro",
        desc: "Un rollo de retratos B/N explorando luz, tono y poca profundidad de campo en film.",
        difficulty: "intermediate",
        relates: "Secciones 3 (Tipos de pelicula), 6 (Enfoque y PDC)",
        objective: "5 retratos B/N fuertes de uno o mas sujetos usando luz de ventana o sombra abierta.",
        settings: "Ilford HP5 400 o Kodak Tri-X 400 | 50mm a f/2–f/2.8 | Mide para el rostro",
        tips: [
          "Mide sobre el rostro del sujeto, no sobre el fondo brillante.",
          "La sombra abierta o luz suave de ventana favorece la piel y evita entrecerrar los ojos.",
          "Enfoca en el ojo cercano usando el prisma partido.",
          "Cuida tu velocidad: mantenla en 1/125s+ a pulso para evitar trepidacion.",
        ],
        deliverables: "5 escaneos de retrato B/N editados mostrando luz intencional y ojos nitidos.",
        critique: [
          "Los ojos estan navaja-nitidos?",
          "El rostro esta bien expuesto (buenos tonos medios, detalle en piel)?",
          "La poca PDC separa al sujeto del fondo?",
          "El rango tonal es agradable (negros reales y blancos limpios)?",
        ],
      },
      {
        id: "film_double_exposure",
        icon: "Sparkle",
        title: "4. Creativo — Doble Exposicion",
        desc: "Usa el truco de exposicion multiple de tu camara (o rebobina-y-redispara) para superponer dos imagenes en un cuadro.",
        difficulty: "advanced",
        relates: "Secciones 2 (Exposicion), 5 (Medicion)",
        objective: "3 dobles exposiciones logradas combinando dos escenas complementarias (ej. un retrato + follaje).",
        settings: "ISO 100–200 | Subexpon CADA capa ~1 paso para que el cuadro combinado no quede muy brillante",
        tips: [
          "Si tu camara no tiene palanca de exposicion multiple, manten el boton de rebobinado mientras montas la palanca de avance para NO avanzar la pelicula.",
          "Subexpon cada exposicion ~1 paso para que el resultado apilado se equilibre.",
          "Empareja un area de sujeto oscura con una textura brillante para que ambas se lean claras.",
          "Planea la composicion: dispara el sujeto contra oscuro, luego la textura contra luz.",
        ],
        deliverables: "3 cuadros de doble exposicion + una nota de que superpusiste y la exposicion usada.",
        critique: [
          "Ambas capas son visibles e intencionales (no embarradas)?",
          "La exposicion combinada esta equilibrada (no quemada)?",
          "El emparejamiento se siente creativo, no aleatorio?",
          "Podrias reproducir el efecto a proposito ahora?",
        ],
      },
    ],

    closing: "El film no reemplazara tu flujo digital — lo complementa. La disciplina de medir a mano, componer para uno de 36 cuadros y esperar tus escaneos te hace un fotografo mas agudo e intencional en TODO medio. Carga un rollo, baja el ritmo y disfruta el oficio.",
  },
};

function LessonCard({ section }) {
  const Icon = ICONS[section.icon] || FilmStrip;
  return (
    <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-4" data-testid={`film-section-${section.id}`}>
      <div className="flex items-center gap-2">
        <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
        <h2 className="font-['Outfit'] text-white text-xl font-medium">{section.title}</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{section.intro}</p>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
        <ol className="space-y-1.5">
          {section.steps.map((s, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs italic leading-relaxed">
          <span className="font-semibold uppercase tracking-widest mr-1.5">{section._tip_label}:</span>
          {section.tip}
        </p>
      </div>
    </motion.div>
  );
}

function ProjectCard({ project, c }) {
  const Icon = ICONS[project.icon] || Sparkle;
  const diffLabel = c[`diff_${project.difficulty}`] || project.difficulty;
  return (
    <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid={`film-project-${project.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
          <h3 className="font-['Outfit'] text-white text-lg font-medium">{project.title}</h3>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${DIFF_BADGE[project.difficulty]}`}>
          {diffLabel}
        </span>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{project.desc}</p>
      <p className="text-[#52525B] text-xs italic">→ {project.relates}</p>

      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Target weight="fill" className="w-3.5 h-3.5 text-[#F5A524]" />
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold">{c.objective_label}</p>
          </div>
          <p className="text-[#A1A1AA] text-xs leading-relaxed">{project.objective}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
          <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.settings_label}</p>
          <p className="text-[#A1A1AA] text-xs leading-relaxed font-mono">{project.settings}</p>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.tips_label}</p>
        <ol className="space-y-1">
          {project.tips.map((t, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
              <span>{t}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.deliverables_label}</p>
        <p className="text-[#A1A1AA] text-xs leading-relaxed">{project.deliverables}</p>
      </div>

      <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-400" />
          <p className="text-emerald-300 text-xs uppercase tracking-widest font-semibold">{c.critique_label}</p>
        </div>
        <ul className="space-y-1">
          {project.critique.map((q, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-emerald-400 shrink-0">☐</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export default function FilmPhotographyPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `film-photography-${lang}.pdf`, [lang]);
  const f = c.featured;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="film-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FilmStrip weight="fill" className="w-6 h-6 text-[#F5A524]" />
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight">{c.page_title}</h1>
              </div>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="film-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Quick jump grid */}
          <motion.div variants={itemAnim} className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="film-subnav">
            {c.sections.map((s) => {
              const Icon = ICONS[s.icon] || FilmStrip;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="bg-[#121214]/60 backdrop-blur-md border border-white/10 hover:border-[#F5A524]/40 rounded-lg p-3 flex items-start gap-2 transition-colors group"
                  data-testid={`film-jump-${s.id}`}
                >
                  <Icon weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
                  <span className="text-[#A1A1AA] group-hover:text-white text-xs leading-snug transition-colors">{s.title}</span>
                </a>
              );
            })}
            <a
              href="#film-stock"
              className="bg-[#F5A524]/10 backdrop-blur-md border border-[#F5A524]/40 hover:border-[#F5A524] rounded-lg p-3 flex items-start gap-2 transition-colors group"
              data-testid="film-jump-film-stock"
            >
              <Palette weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
              <span className="text-[#F5A524] group-hover:text-white text-xs leading-snug font-semibold transition-colors">{c.filmstock_title}</span>
            </a>
            <a
              href="#cameras"
              className="bg-[#F5A524]/10 backdrop-blur-md border border-[#F5A524]/40 hover:border-[#F5A524] rounded-lg p-3 flex items-start gap-2 transition-colors group"
              data-testid="film-jump-cameras"
            >
              <Camera weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
              <span className="text-[#F5A524] group-hover:text-white text-xs leading-snug font-semibold transition-colors">{c.cameras_title}</span>
            </a>
            <a
              href="#projects"
              className="bg-[#F5A524]/10 backdrop-blur-md border border-[#F5A524]/40 hover:border-[#F5A524] rounded-lg p-3 flex items-start gap-2 transition-colors group"
              data-testid="film-jump-projects"
            >
              <Sparkle weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
              <span className="text-[#F5A524] group-hover:text-white text-xs leading-snug font-semibold transition-colors">{c.projects_title}</span>
            </a>
          </motion.div>

          {/* Lessons */}
          {c.sections.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-20">
              <LessonCard section={{ ...s, _tip_label: c.pro_tip_label }} />
            </div>
          ))}

          {/* Film stock buying guide */}
          <motion.div variants={itemAnim} id="film-stock" className="scroll-mt-20 bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid="film-stock-guide">
            <div className="flex items-center gap-2">
              <Palette weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.filmstock_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.filmstock_sub}</p>
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full text-left min-w-[640px]">
                <thead>
                  <tr className="bg-[#0A0A0A]">
                    {[c.filmstock_h_name, c.filmstock_h_type, c.filmstock_h_use, c.filmstock_h_grain, c.filmstock_h_price].map((h, i) => (
                      <th key={i} className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {c.filmstock_rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-2 text-white text-xs font-medium whitespace-nowrap">{r.name}</td>
                      <td className="px-3 py-2 text-[#A1A1AA] text-xs">{r.type}</td>
                      <td className="px-3 py-2 text-[#A1A1AA] text-xs">{r.use}</td>
                      <td className="px-3 py-2 text-[#A1A1AA] text-xs">{r.grain}</td>
                      <td className="px-3 py-2 text-[#F5A524] text-xs font-mono font-bold whitespace-nowrap">{r.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Developing options */}
          <motion.div variants={itemAnim} id="develop-options" className="scroll-mt-20 bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid="film-develop-guide">
            <div className="flex items-center gap-2">
              <Flask weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.develop_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.develop_sub}</p>
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full text-left min-w-[560px]">
                <thead>
                  <tr className="bg-[#0A0A0A]">
                    <th className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{c.develop_h_route}</th>
                    <th className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{c.develop_h_time}</th>
                    <th className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{c.develop_h_cost}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.develop_rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-2">
                        <p className="text-[#D4D4D8] text-xs font-medium">{r.route}</p>
                        <p className="text-[#52525B] text-[11px]">{r.note}</p>
                      </td>
                      <td className="px-3 py-2 text-[#A1A1AA] text-xs whitespace-nowrap">{r.time}</td>
                      <td className="px-3 py-2 text-[#F5A524] text-xs font-mono font-bold whitespace-nowrap">{r.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Cameras header */}
          <motion.div variants={itemAnim} id="cameras" className="scroll-mt-20 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Camera weight="fill" className="w-6 h-6 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-2xl sm:text-3xl font-semibold tracking-tight">{c.cameras_title}</h2>
            </div>
            <p className="text-[#71717A] text-sm max-w-3xl">{c.cameras_sub}</p>
          </motion.div>

          {/* Featured AE-1 Program */}
          <motion.div variants={itemAnim} className="bg-gradient-to-br from-[#F5A524]/10 via-[#121214]/80 to-[#121214]/80 backdrop-blur-md border-2 border-[#F5A524]/40 rounded-xl p-6 sm:p-8 space-y-5" data-testid="film-featured-ae1">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-[#0A0A0A] bg-[#F5A524] px-2.5 py-1 rounded-full">
                <Star weight="fill" className="w-3 h-3" />{f.label}
              </span>
              <h3 className="font-['Outfit'] text-white text-2xl font-semibold tracking-tight mt-3">{f.name}</h3>
              <p className="text-[#F5A524] text-xs font-mono mt-1">{f.type}</p>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{f.blurb}</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">Specs</p>
                <dl className="space-y-1.5">
                  {f.specs.map((sp, i) => (
                    <div key={i} className="flex justify-between gap-3 text-xs">
                      <dt className="text-[#71717A] shrink-0">{sp.k}</dt>
                      <dd className="text-[#D4D4D8] text-right">{sp.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{f.settings_label}</p>
                <dl className="space-y-1.5">
                  {f.settings.map((sp, i) => (
                    <div key={i} className="flex justify-between gap-3 text-xs">
                      <dt className="text-[#71717A] shrink-0">{sp.k}</dt>
                      <dd className="text-[#D4D4D8] text-right">{sp.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
              <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{f.steps_label}</p>
              <ol className="space-y-1.5">
                {f.steps.map((st, i) => (
                  <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
                    <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
                    <span>{st}</span>
                  </li>
                ))}
              </ol>
            </div>
          </motion.div>

          {/* Camera roundup grid */}
          <motion.div variants={itemAnim} className="grid sm:grid-cols-2 gap-3" data-testid="film-camera-roundup">
            {c.cameras.map((cam, i) => (
              <div key={i} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Camera weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0" />
                  <p className="text-white text-sm font-medium">{cam.name}</p>
                </div>
                <p className="text-[#F5A524] text-[11px] font-mono mb-0.5">{cam.years}</p>
                <p className="text-[#52525B] text-[11px] mb-1.5">{cam.type}</p>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">{cam.note}</p>
              </div>
            ))}
          </motion.div>

          {/* Sunny 16 cheat sheet */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6 space-y-3" data-testid="film-sunny16">
            <div className="flex items-center gap-2">
              <Sun weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.sunny16_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.sunny16_sub}</p>
            <div className="overflow-hidden rounded-lg border border-white/10">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[#0A0A0A]">
                    <th className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{c.sunny16_cond_label}</th>
                    <th className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold px-3 py-2">{c.sunny16_aperture_label}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.sunny16_rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-2">
                        <p className="text-[#D4D4D8] text-xs">{r.cond}</p>
                        <p className="text-[#52525B] text-[11px]">{r.note}</p>
                      </td>
                      <td className="px-3 py-2 text-[#F5A524] font-mono font-bold text-sm whitespace-nowrap">{r.aperture}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3 flex items-start gap-2">
              <Gauge weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
              <p className="text-[#F5A524] text-xs leading-relaxed">{c.sunny16_note}</p>
            </div>
          </motion.div>

          {/* Projects header */}
          <motion.div variants={itemAnim} id="projects" className="scroll-mt-20 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle weight="fill" className="w-6 h-6 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-2xl sm:text-3xl font-semibold tracking-tight">{c.projects_title}</h2>
            </div>
            <p className="text-[#71717A] text-sm max-w-3xl">{c.projects_sub}</p>
          </motion.div>

          {c.projects.map((p) => (
            <ProjectCard key={p.id} project={p} c={c} />
          ))}

          {/* Closing */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.closing}</p>
          </motion.div>

          {/* Share on Galeria Social CTA */}
          <motion.div variants={itemAnim} data-testid="film-galeria-cta">
            <GaleriaSocialCard />
          </motion.div>
        </motion.div>
      </div>

      {/* Hidden printable version */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        {c.sections.map((s) => (
          <div key={s.id} className="mb-5 break-inside-avoid">
            <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{s.title}</h2>
            <p className="text-sm text-gray-700 mb-2">{s.intro}</p>
            <ol className="list-decimal ml-5 space-y-1 mb-2">
              {s.steps.map((st, j) => <li key={j} className="text-xs text-gray-700">{st}</li>)}
            </ol>
            <p className="text-xs text-gray-700 italic"><strong>{c.pro_tip_label}:</strong> {s.tip}</p>
          </div>
        ))}

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.filmstock_title}</h1>
        <p className="text-center text-gray-500 mb-3 text-sm">{c.filmstock_sub}</p>
        <table className="w-full text-left mb-2">
          <thead>
            <tr>
              {[c.filmstock_h_name, c.filmstock_h_type, c.filmstock_h_use, c.filmstock_h_grain, c.filmstock_h_price].map((h, i) => (
                <th key={i} className="text-xs font-bold border-b border-gray-300 pb-1">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.filmstock_rows.map((r, i) => (
              <tr key={i}>
                <td className="text-xs text-gray-800 font-semibold py-0.5">{r.name}</td>
                <td className="text-xs text-gray-700 py-0.5">{r.type}</td>
                <td className="text-xs text-gray-700 py-0.5">{r.use}</td>
                <td className="text-xs text-gray-700 py-0.5">{r.grain}</td>
                <td className="text-xs font-bold py-0.5">{r.price}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.develop_title}</h1>
        <p className="text-center text-gray-500 mb-3 text-sm">{c.develop_sub}</p>
        <table className="w-full text-left mb-2">
          <thead>
            <tr>
              <th className="text-xs font-bold border-b border-gray-300 pb-1">{c.develop_h_route}</th>
              <th className="text-xs font-bold border-b border-gray-300 pb-1">{c.develop_h_time}</th>
              <th className="text-xs font-bold border-b border-gray-300 pb-1">{c.develop_h_cost}</th>
            </tr>
          </thead>
          <tbody>
            {c.develop_rows.map((r, i) => (
              <tr key={i}>
                <td className="text-xs text-gray-700 py-0.5">{r.route} <span className="text-gray-400">({r.note})</span></td>
                <td className="text-xs text-gray-700 py-0.5">{r.time}</td>
                <td className="text-xs font-bold py-0.5">{r.cost}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.cameras_title}</h1>
        <div className="mb-4 break-inside-avoid">
          <h2 className="text-lg font-bold mb-1">{f.name} <span className="text-xs italic text-gray-500">— {f.label}</span></h2>
          <p className="text-xs italic text-gray-500 mb-1">{f.type}</p>
          <p className="text-sm text-gray-700 mb-2">{f.blurb}</p>
          <ol className="list-decimal ml-5 space-y-0.5">
            {f.steps.map((st, i) => <li key={i} className="text-xs text-gray-700">{st}</li>)}
          </ol>
        </div>
        {c.cameras.map((cam, i) => (
          <div key={i} className="mb-2">
            <p className="text-sm font-bold">{cam.name} <span className="text-xs italic text-gray-500">— {cam.years} · {cam.type}</span></p>
            <p className="text-xs text-gray-700">{cam.note}</p>
          </div>
        ))}

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.sunny16_title}</h1>
        <p className="text-center text-gray-500 mb-3 text-sm">{c.sunny16_sub}</p>
        <table className="w-full text-left mb-2">
          <thead>
            <tr><th className="text-xs font-bold border-b border-gray-300 pb-1">{c.sunny16_cond_label}</th><th className="text-xs font-bold border-b border-gray-300 pb-1">{c.sunny16_aperture_label}</th></tr>
          </thead>
          <tbody>
            {c.sunny16_rows.map((r, i) => (
              <tr key={i}><td className="text-xs text-gray-700 py-0.5">{r.cond} <span className="text-gray-400">({r.note})</span></td><td className="text-xs font-bold py-0.5">{r.aperture}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-700 italic mb-2">{c.sunny16_note}</p>

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.projects_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.projects_sub}</p>
        {c.projects.map((p) => (
          <div key={p.id} className="mb-5 break-inside-avoid">
            <h2 className="text-lg font-bold mb-1 border-b-2 border-gray-200 pb-1">
              {p.title} <span className="text-xs italic text-gray-500">— {c[`diff_${p.difficulty}`]}</span>
            </h2>
            <p className="text-sm text-gray-700 mb-1">{p.desc}</p>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.objective_label}:</strong> {p.objective}</p>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.settings_label}:</strong> {p.settings}</p>
            <p className="text-xs font-bold text-gray-800">{c.tips_label}:</p>
            <ol className="list-decimal ml-5 space-y-0.5 mb-1">
              {p.tips.map((t, i) => <li key={i} className="text-xs text-gray-700">{t}</li>)}
            </ol>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.deliverables_label}:</strong> {p.deliverables}</p>
            <p className="text-xs font-bold text-gray-800">{c.critique_label}:</p>
            <ul className="ml-5 space-y-0.5 mb-2">
              {p.critique.map((q, i) => <li key={i} className="text-xs text-gray-700">☐ {q}</li>)}
            </ul>
          </div>
        ))}
        <p className="text-sm text-gray-700 italic mt-4">{c.closing}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
